import * as Sentry from '@sentry/react'
import { SIX_PASSKEY_ID, SIX_PASSKEY_OWNER, SIX_PASSKEY_SETUP_SKIPPED } from '../storage/keys'

export type RegisterPasskeyOptions = {
    userId: string
    emailId: string
    name: string
}

function generateRandomChallenge(length = 32) {
    const randomValues = new Uint8Array(length)
    window.crypto.getRandomValues(randomValues)
    return randomValues
}

function bufferToBase64Url(buffer: ArrayBuffer) {
    const bytes = new Uint8Array(buffer)
    let str = ''
    for (let i = 0; i < bytes.length; i++) str += String.fromCharCode(bytes[i])
    return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function base64UrlToBuffer(base64url: string) {
    const normalized = base64url.replace(/-/g, '+').replace(/_/g, '/')
    const remainder = normalized.length % 4
    const padding = remainder === 0 ? '' : '='.repeat(4 - remainder)
    const base64 = normalized + padding
    const binary = atob(base64)
    const len = binary.length
    const bytes = new Uint8Array(len)
    for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i)
    return bytes.buffer
}

export function isWebAuthnSupported() {
    return (
        typeof navigator !== 'undefined' &&
        !!navigator.credentials &&
        typeof navigator.credentials.create === 'function' &&
        typeof navigator.credentials.get === 'function'
    )
}

export function hasStoredPasskey() {
    try {
        return !!localStorage.getItem(SIX_PASSKEY_ID)
    } catch {
        return false
    }
}

export function clearStoredPasskey() {
    try {
        localStorage.removeItem(SIX_PASSKEY_ID)
        localStorage.removeItem(SIX_PASSKEY_OWNER)
    } catch {
        // ignore
    }
}

export function getPasskeySetupSkipped() {
    try {
        return localStorage.getItem(SIX_PASSKEY_SETUP_SKIPPED) === '1'
    } catch {
        return false
    }
}

export function setPasskeySetupSkipped(skipped: boolean) {
    try {
        localStorage.setItem(SIX_PASSKEY_SETUP_SKIPPED, skipped ? '1' : '0')
    } catch {
        // ignore
    }
}

export function clearStoredPasskeyIfUserMismatch(userId?: string | null) {
    try {
        if (!userId) return false
        const owner = localStorage.getItem(SIX_PASSKEY_OWNER)
        if (owner !== userId) {
            clearStoredPasskey()
            return true
        }
        return false
    } catch {
        return false
    }
}

function getStoredPasskeyId() {
    try {
        return localStorage.getItem(SIX_PASSKEY_ID)
    } catch {
        return null
    }
}

export function useIsStandalone() {
    try {
        if (typeof window === 'undefined') return false
        const iosStandalone = (navigator as any).standalone === true
        const displayStandalone = window.matchMedia('(display-mode: standalone)').matches
        return iosStandalone || displayStandalone
    } catch {
        return false
    }
}

export async function registerPasskey(opts: RegisterPasskeyOptions) {
    if (!isWebAuthnSupported()) {
        return false
    }

    try {
        const userIdBytes = new TextEncoder().encode(opts.userId).slice(0, 64)

        const publicKey: PublicKeyCredentialCreationOptions = {
            challenge: generateRandomChallenge(),
            rp: { name: 'Six App', id: window.location.hostname },
            user: { id: userIdBytes, name: opts.emailId, displayName: opts.name },
            pubKeyCredParams: [
                { type: 'public-key', alg: -7 },
                { type: 'public-key', alg: -257 },
            ],
            timeout: 60000,
            authenticatorSelection: {
                residentKey: 'preferred',
                requireResidentKey: false,
                userVerification: 'preferred',
            },
            attestation: 'none',
            extensions: { credProps: true },
        }

        const cred = (await navigator.credentials.create({ publicKey })) as PublicKeyCredential | null
        if (!cred) return false

        const rawId = cred.rawId
        const idStr = bufferToBase64Url(rawId)
        try {
            localStorage.setItem(SIX_PASSKEY_ID, idStr)
            if (opts?.userId) {
                localStorage.setItem(SIX_PASSKEY_OWNER, opts.userId)
            }
            localStorage.removeItem(SIX_PASSKEY_SETUP_SKIPPED)
        } catch {
            // ignore
        }
        return true
    } catch (err: any) {
        Sentry.captureException(err, {
            tags: { feature: 'webauthn' },
            extra: {
                name: err?.name,
                message: err?.message,
            },
        })
        return false
    }
}

export async function runDeviceAuth(): Promise<boolean> {
    if (!isWebAuthnSupported()) {
        return false
    }
    try {
        const idStr = getStoredPasskeyId()
        if (!idStr) {
            return false
        }
        const rawId = base64UrlToBuffer(idStr)
        const publicKey: PublicKeyCredentialRequestOptions = {
            challenge: generateRandomChallenge(),
            allowCredentials: [{ id: rawId, type: 'public-key' }],
            timeout: 60000,
            userVerification: 'preferred',
        }

        const assertion = (await navigator.credentials.get({ publicKey })) as PublicKeyCredential | null
        if (!assertion) return false
        return true
    } catch (err: any) {
        Sentry.captureException(err, {
            tags: { feature: 'webauthn' },
            extra: {
                name: err?.name,
                message: err?.message,
            },
        })
        return false
    }
}

export default {
    isWebAuthnSupported,
    registerPasskey,
    runDeviceAuth,
    hasStoredPasskey,
    clearStoredPasskey,
    clearStoredPasskeyIfUserMismatch,
    getPasskeySetupSkipped,
    setPasskeySetupSkipped,
}
